import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import AdminLeagueStatistics from './AdminLeagueStatistics';
import AdminLeagueAnalytics from './AdminLeagueAnalytics';
import AdminAttentionCenter from './AdminAttentionCenter';
import AdminRegistrationAnalyticsSection from './AdminRegistrationAnalyticsSection';
import AdminRegistrationPaymentExportSection from './AdminRegistrationPaymentExportSection';
import AdminPromoCodesSection from './AdminPromoCodesSection';
import AdminPartnerCommunicationsSection from './AdminPartnerCommunicationsSection';
import AdminSettingsAnalytics from './AdminSettingsAnalytics';
import AdminPartnerClubReport from './AdminPartnerClubReport';
import { consumeAdminNavHandoff, setAdminNavHandoff } from '../utils/adminNavFilters';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const BRAND = { blue: '#043663', muted: '#64748b' };

const Page = styled.div`
  width: 100%;
  max-width: none;
  min-width: 0;
`;

const Header = styled.div`
  margin-bottom: 1rem;
  h2 {
    margin: 0 0 0.35rem;
    font-size: 1.65rem;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.02em;
  }
  p {
    margin: 0;
    color: ${BRAND.muted};
    font-size: 0.9rem;
    max-width: 48rem;
    line-height: 1.45;
  }
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 1.15rem;
`;

const Tab = styled.button`
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? BRAND.blue : '#e2e8f0')};
  background: ${(p) => (p.$active ? BRAND.blue : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : '#475569')};
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
`;

/** Legacy section id → hub tab */
export const ANALYTICS_HUB_SECTION_TO_TAB = {
  'league-statistics': 'league',
  analytics: 'deep-dive',
  attention: 'attention',
  'registration-analytics': 'registrations',
  'promo-codes': 'promo',
  'partner-communications': 'partners',
  'settings-analytics': 'exports',
};

const TAB_DEFS = [
  { id: 'league', label: 'League', permission: 'stats' },
  { id: 'deep-dive', label: 'Breakdowns', permission: 'stats' },
  { id: 'partner-clubs', label: 'Partner clubs', permission: 'stats' },
  { id: 'attention', label: 'Attention', permission: 'stats' },
  { id: 'registrations', label: 'Registrations', permission: 'registration_analytics' },
  { id: 'payment-status', label: 'Payment status', permission: 'registration_analytics' },
  { id: 'promo', label: 'Promo & loyalty', permission: 'promo_codes' },
  { id: 'partners', label: 'Partners', permission: 'partner_communications' },
  { id: 'exports', label: 'Exports', permission: 'settings_general' },
];

/**
 * True if the admin can see the Analytics hub at all (any child tab permission).
 */
export function canAccessAnalyticsHub(canReadSection) {
  if (typeof canReadSection !== 'function') return false;
  return TAB_DEFS.some((t) => canReadSection(t.permission));
}

export default function AdminAnalyticsHub({ onNavigate, canReadSection: canReadProp }) {
  const { adminUser } = useAdminAuth();
  const canRead =
    canReadProp ||
    ((sectionKey) => {
      if (!adminUser) return false;
      if (adminUser.isSuperAdmin) return true;
      const perms = adminUser.permissions;
      return !!(perms && perms[sectionKey] && perms[sectionKey].read === true);
    });

  const allowedTabs = useMemo(
    () => TAB_DEFS.filter((t) => canRead(t.permission)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adminUser?.permissions, adminUser?.isSuperAdmin, canReadProp]
  );

  const [tab, setTab] = useState(() => allowedTabs[0]?.id || 'league');

  useEffect(() => {
    if (!allowedTabs.length) return;
    if (!allowedTabs.some((t) => t.id === tab)) {
      setTab(allowedTabs[0].id);
    }
  }, [allowedTabs, tab]);

  useEffect(() => {
    const h = consumeAdminNavHandoff();
    if (!h) return;
    if (h.analyticsHubTab && allowedTabs.some((t) => t.id === h.analyticsHubTab)) {
      setTab(h.analyticsHubTab);
      if (h.analyticsHubTab === 'deep-dive' && h.analyticsTab) {
        setAdminNavHandoff({
          analyticsTab: h.analyticsTab,
          seasonId: h.seasonId,
          canton: h.canton,
        });
      }
      return;
    }
    if (h.section && ANALYTICS_HUB_SECTION_TO_TAB[h.section]) {
      const mapped = ANALYTICS_HUB_SECTION_TO_TAB[h.section];
      if (allowedTabs.some((t) => t.id === mapped)) {
        setTab(mapped);
        if (mapped === 'deep-dive' && h.analyticsTab) {
          setAdminNavHandoff({
            analyticsTab: h.analyticsTab,
            seasonId: h.seasonId,
            canton: h.canton,
          });
        }
      }
      return;
    }
    if (h.analyticsTab) {
      if (allowedTabs.some((t) => t.id === 'deep-dive')) {
        setTab('deep-dive');
        setAdminNavHandoff({
          analyticsTab: h.analyticsTab,
          seasonId: h.seasonId,
          canton: h.canton,
        });
      }
    }
  }, [allowedTabs]);

  if (!allowedTabs.length) {
    return (
      <Page>
        <Header>
          <h2>Analytics</h2>
          <p>You don&apos;t have access to any analytics sections.</p>
        </Header>
      </Page>
    );
  }

  return (
    <Page>
      <Header>
        <h2>Analytics</h2>
        <p>
          League reports, partner club utilisation, attention, registration sources, promo tracking,
          partners, and exports — in one place.
        </p>
      </Header>

      <Tabs>
        {allowedTabs.map((t) => (
          <Tab key={t.id} type="button" $active={tab === t.id} onClick={() => setTab(t.id)}>
            {t.label}
          </Tab>
        ))}
      </Tabs>

      {tab === 'league' ? <AdminLeagueStatistics onNavigate={onNavigate} /> : null}
      {tab === 'deep-dive' ? <AdminLeagueAnalytics /> : null}
      {tab === 'partner-clubs' ? <AdminPartnerClubReport /> : null}
      {tab === 'attention' ? <AdminAttentionCenter onNavigate={onNavigate} /> : null}
      {tab === 'registrations' ? <AdminRegistrationAnalyticsSection /> : null}
      {tab === 'payment-status' ? <AdminRegistrationPaymentExportSection /> : null}
      {tab === 'promo' ? <AdminPromoCodesSection /> : null}
      {tab === 'partners' ? <AdminPartnerCommunicationsSection /> : null}
      {tab === 'exports' ? <AdminSettingsAnalytics /> : null}
    </Page>
  );
}
